/**
 * #344 — the Place-Order gate for the Game Detail ticket, as a pure function.
 *
 * `disabled` reproduces the boolean the ticket computed inline before the
 * extraction, clause for clause and in the same short-circuit order. `reason`
 * names whichever clause fired first; it is display/test surface only, and
 * nothing may consult it to decide whether to submit.
 *
 * A disconnected account and a missing ticker are NOT gate states — the ticket
 * early-returns a message instead of rendering the form at all.
 */
import { POLY_MIN_ORDER_CONTRACTS } from '@/lib/polymarket/closePosition';
import type { LimitRejectionBreach } from '@/lib/limits';
import type { Venue } from '@/types';

/** Kalshi accepts a single contract; Polymarket's CLOB rejects below 5;
 *  Polymarket US publishes a per-market `minimumTradeQty` (fractional
 *  contracts allowed — 0.01 seen live), so the floor comes off the book and
 *  falls back to 1 when the frame carries none. */
export function minQtyFor(
  venue: Venue,
  book?: { minimumTradeQty?: number | string | null } | null,
): number {
  if (venue === 'kalshi') return 1;
  if (venue === 'polymarket') return POLY_MIN_ORDER_CONTRACTS;
  const raw = book?.minimumTradeQty;
  const n = raw == null ? NaN : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export type SubmitBlockReason =
  | 'submitting'
  | 'limit-breach'
  | 'poly-below-floor'
  | 'polyus-below-floor'
  | 'polyus-missing-slug'
  | 'ladder-no-valid-legs'
  | 'missing-qty'
  | 'missing-price';

export interface SubmitGateInput {
  submitting: boolean;
  effectiveBreach: LimitRejectionBreach | null;
  isKalshiVenue: boolean;
  /** F3: the third venue. Absent ⇒ derived from `isKalshiVenue` (intl poly
   *  when false), which keeps every pre-F3 caller and test byte-identical. */
  venue?: Venue;
  /** Polymarket US floor (from the book's minimumTradeQty). */
  minQty?: number;
  /** Polymarket US: the order addresses a market slug; no slug, no order. */
  hasMarketSlug?: boolean;
  count: number;
  ladderActive: boolean;
  /** `ladderSummary?.legs ?? 0` — how many rungs actually validated. */
  ladderLegs: number;
  qty: string;
  orderType: 'limit' | 'market';
  price: string;
}

export function submitGate(input: SubmitGateInput): {
  disabled: boolean;
  reason: SubmitBlockReason | null;
} {
  const { submitting, effectiveBreach, isKalshiVenue, count, ladderActive, ladderLegs, qty, orderType, price } =
    input;
  const venue: Venue = input.venue ?? (isKalshiVenue ? 'kalshi' : 'polymarket');

  if (submitting) return { disabled: true, reason: 'submitting' };
  if (effectiveBreach) return { disabled: true, reason: 'limit-breach' };
  // Polymarket floor (belt to the blur-snap + submit-path braces). Guarded on
  // count > 0 so an empty poly ticket reports "no qty", not "below the floor".
  if (venue === 'polymarket' && count > 0 && count < POLY_MIN_ORDER_CONTRACTS) {
    return { disabled: true, reason: 'poly-below-floor' };
  }
  if (venue === 'polymarket_us') {
    if (input.hasMarketSlug === false) return { disabled: true, reason: 'polyus-missing-slug' };
    const floor = input.minQty ?? 1;
    if (count > 0 && count < floor) return { disabled: true, reason: 'polyus-below-floor' };
  }
  // Ladder: enabled as long as at least one rung is valid. Single order:
  // require qty (and a price for limit) as before.
  if (ladderActive) {
    return ladderLegs === 0
      ? { disabled: true, reason: 'ladder-no-valid-legs' }
      : { disabled: false, reason: null };
  }
  if (!qty) return { disabled: true, reason: 'missing-qty' };
  if (orderType === 'limit' && !price) return { disabled: true, reason: 'missing-price' };
  return { disabled: false, reason: null };
}
